/**
 * Робот исходящего обзвона: выгрузка НДЗ и приём результатов.
 *
 * В робот уходят контакты, у которых кончились круги (§6.10). Результат
 * принимается с ключом дедупликации: повторная доставка вебхука не должна
 * закрыть контакт дважды и не должна создать второй лид.
 */
import { z } from 'zod';
import { bigIdSchema, isoDateTimeSchema, phoneE164Schema, refIdSchema } from './common.dto.js';
/** held — ждёт решения админа (Саша или другой робот), exported — скачан для другого робота (24.09.2026) */
export const robotQueueStatusSchema = z.enum(['pending', 'sent', 'done', 'failed', 'held', 'exported']);
export const robotQueueRowSchema = z.object({
    id: bigIdSchema,
    queueItemId: bigIdSchema,
    contactId: bigIdSchema,
    phone: phoneE164Schema,
    sourceId: refIdSchema,
    provider: z.string(),
    campaignId: z.string().nullable(),
    status: robotQueueStatusSchema,
    externalId: z.string().nullable(),
    attempts: z.number().int().min(0),
    sentAt: isoDateTimeSchema.nullable(),
    finishedAt: isoDateTimeSchema.nullable(),
    lastError: z.string().nullable(),
});
export const robotQueueQuerySchema = z.object({
    status: robotQueueStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
/** GET /api/robot/export — то, что забирает робот: батч 200 строк (§6.10) */
export const robotExportQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(500).default(200),
    campaignId: z.string().max(120).optional(),
});
export const robotExportItemSchema = z.object({
    /** Наш ключ: с ним результат вернётся обратно и найдёт свой контакт */
    robotQueueId: bigIdSchema,
    phone: phoneE164Schema,
    fullName: z.string().nullable(),
    regionCode: z.string().nullable(),
});
export const robotExportResponseSchema = z.object({
    items: z.array(robotExportItemSchema),
    campaignId: z.string().nullable(),
});
/**
 * GET /api/robot/results — последние результаты, которые прислал робот.
 *
 * Отдельный список, а не поле в очереди: строка очереди живёт до закрытия,
 * а результатов по ней может прийти несколько (повтор доставки отсекается
 * по dedup_key, но провайдер шлёт и уточнения). Экран отвечает на вопрос
 * «робот вообще присылает что-нибудь», поэтому список идёт от свежих.
 */
export const robotResultsQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const robotResultRowSchema = z.object({
    id: bigIdSchema,
    robotQueueId: bigIdSchema.nullable(),
    /** Номер известен, только если результат нашёл свою строку очереди */
    phone: phoneE164Schema.nullable(),
    externalId: z.string().nullable(),
    isLead: z.boolean(),
    /** Сырой статус робота: показывается как есть, своих трактовок у нас нет */
    callStatus: z.string().nullable(),
    talkSec: z.number().int().min(0),
    receivedAt: isoDateTimeSchema,
});
export const robotResultsResponseSchema = z.object({ items: z.array(robotResultRowSchema) });
/** POST /api/robot/results — вебхук робота */
export const robotResultBodySchema = z.object({
    robotQueueId: bigIdSchema.optional(),
    externalId: z.string().max(200).optional(),
    /** Ключ дедупликации доставки: повтор вебхука игнорируется по нему */
    dedupKey: z.string().min(6).max(200),
    isLead: z.boolean(),
    /** Сырой статус робота: хранится как есть, чтобы можно было пересчитать */
    callStatus: z.string().max(80).nullable(),
    talkSec: z.number().int().min(0).max(24 * 3600),
    payload: z.record(z.string(), z.unknown()).optional(),
    /**
     * false — промежуточный результат (робот поговорил, но лида нет, попытки ещё
     * остались): сохраняется для отчёта, контакт не закрывается. По умолчанию true.
     */
    final: z.boolean().optional(),
});
export const robotResultResponseSchema = z.object({
    ok: z.literal(true),
    /** Повторная доставка того же результата: обработки не было */
    duplicate: z.boolean(),
});
/** GET /api/robot/held — НДЗ вечерней передачи, ждущие решения админа (24.09.2026) */
export const robotHeldSummarySchema = z.object({
    total: z.number().int().min(0),
    byDay: z.array(z.object({ day: z.string(), count: z.number().int().min(0) })),
    asOf: isoDateTimeSchema,
    /** Последняя выгрузка для другого робота за сутки: файл можно скачать ещё раз */
    lastExport: z.object({ at: isoDateTimeSchema, count: z.number().int().min(0) }).nullable(),
});
/** POST /api/robot/held/send и /export: `upTo` — момент списка, который видел админ */
export const robotHeldActionBodySchema = z.object({
    upTo: isoDateTimeSchema.optional(),
});
export const robotHeldSendResponseSchema = z.object({ sent: z.number().int().min(0) });
//# sourceMappingURL=robot.dto.js.map